'use strict';
/**
 * azure-functions/shared/gap/reportRows.cjs
 *
 * ADR-0014 option A: a gap assessment's report lives in its own row
 * (dbo.tenant_gap_reports, shared/gap/reportStore.js), not on
 * state.gapAnalyses[].report in the tenant_state blob. One definition, used by
 * the server (state assembly, state-write) and the browser (save path), so
 * both agree on which field is the report and what is never saved through the
 * blob.
 *
 *   mergeGapReports    state assembly: the row's report replaces the blob
 *                      copy; an assessment with no row keeps its blob copy
 *                      (not yet backfilled, or migration 085 not applied).
 *   stripGapReports    the browser's save path: the report and its row
 *                      version are never part of a blob save — the report is
 *                      written only by gap-reports-write and the worker.
 *   restoreGapReports  state-write: whatever a payload carries, each
 *                      assessment keeps the report stored in the blob
 *                      (restore, don't reject — shared/serverOwned/keys.js).
 *
 * Why the blob copy can never be newer than the row (the declared rule,
 * ADR-0014 §7): once this code is deployed the blob copy cannot change
 * (restoreGapReports), every row write starts from the merged view (row,
 * else blob), and the backfill copies only for a tenant whose blob carries
 * SERVER_OWNED_MARKER — written by state-write from this code on. So the row,
 * when it exists, always wins.
 */

const REPORT_FIELD = 'report';
const VERSION_FIELD = 'reportRowVersion';
/** Blob key state-write stamps once it restores reports: `{ since: <ISO time of the first such save> }`. */
const SERVER_OWNED_MARKER = 'gapReportsServerOwnedSince';

function isObject(v) {
  return Boolean(v) && typeof v === 'object' && !Array.isArray(v);
}

/**
 * @param {object[]} gapAnalyses  the blob's state.gapAnalyses
 * @param {Map<string, { report: object, rowVersion: string }>} reports
 * @returns {object[]} a new array; records without a row are returned as-is
 */
function mergeGapReports(gapAnalyses, reports) {
  if (!Array.isArray(gapAnalyses)) return gapAnalyses;
  if (!reports || reports.size === 0) return gapAnalyses;
  return gapAnalyses.map((ga) => {
    if (!isObject(ga) || ga.id === undefined || ga.id === null) return ga;
    const row = reports.get(String(ga.id));
    if (!row) return ga;
    return { ...ga, [REPORT_FIELD]: row.report, [VERSION_FIELD]: row.rowVersion };
  });
}

const stripMemo = new WeakMap();

function stripRecord(ga) {
  if (!isObject(ga)) return ga;
  if (!(REPORT_FIELD in ga) && !(VERSION_FIELD in ga)) return ga;
  const out = { ...ga };
  delete out[REPORT_FIELD];
  delete out[VERSION_FIELD];
  return out;
}

/**
 * The state without any gap report or report row version. Returns the same
 * object when there is nothing to strip, and the same stripped array for the
 * same input array, so identity comparisons in the save loop stay stable.
 */
function stripGapReports(state) {
  if (!isObject(state) || !Array.isArray(state.gapAnalyses)) return state;
  const list = state.gapAnalyses;
  let stripped = stripMemo.get(list);
  if (!stripped) {
    const anything = list.some((ga) => isObject(ga) && (REPORT_FIELD in ga || VERSION_FIELD in ga));
    stripped = anything ? list.map(stripRecord) : list;
    stripMemo.set(list, stripped);
  }
  if (stripped === list && !(SERVER_OWNED_MARKER in state)) return state;
  const out = { ...state, gapAnalyses: stripped };
  delete out[SERVER_OWNED_MARKER];
  return out;
}

/**
 * Put the stored report back on every incoming assessment. Mutates
 * `incoming` (state-write's payload array) in place.
 *
 * - an assessment the blob already holds keeps the blob's report (or none);
 * - a NEW assessment carries no report: reports are only ever created by
 *   the worker and gap-reports-write;
 * - the row version is never stored.
 *
 * @param {object[]} incoming  payload state.gapAnalyses
 * @param {object[]|null} stored  the blob's stored state.gapAnalyses
 * @returns {{ rewritten: string[] }} ids whose payload carried a report that differed
 */
function restoreGapReports(incoming, stored) {
  const rewritten = [];
  if (!Array.isArray(incoming)) return { rewritten };
  const byId = new Map();
  for (const ga of Array.isArray(stored) ? stored : []) {
    if (isObject(ga) && ga.id !== undefined && ga.id !== null) byId.set(String(ga.id), ga);
  }
  for (const ga of incoming) {
    if (!isObject(ga)) continue;
    const before = byId.get(String(ga.id));
    const keep = before && isObject(before[REPORT_FIELD]) ? before[REPORT_FIELD] : null;
    const sent = REPORT_FIELD in ga ? ga[REPORT_FIELD] : undefined;
    if (sent !== undefined && JSON.stringify(sent ?? null) !== JSON.stringify(keep)) {
      rewritten.push(String(ga.id));
    }
    if (keep) ga[REPORT_FIELD] = keep;
    else if (before && REPORT_FIELD in before) ga[REPORT_FIELD] = before[REPORT_FIELD];
    else delete ga[REPORT_FIELD];
    delete ga[VERSION_FIELD];
  }
  return { rewritten };
}

/** Ids the stored blob held that the incoming payload no longer does. */
function removedAssessmentIds(incoming, stored) {
  const keep = new Set(
    (Array.isArray(incoming) ? incoming : [])
      .filter((ga) => isObject(ga) && ga.id !== undefined && ga.id !== null)
      .map((ga) => String(ga.id))
  );
  return (Array.isArray(stored) ? stored : [])
    .filter(
      (ga) => isObject(ga) && ga.id !== undefined && ga.id !== null && !keep.has(String(ga.id))
    )
    .map((ga) => String(ga.id));
}

module.exports = {
  REPORT_FIELD,
  VERSION_FIELD,
  SERVER_OWNED_MARKER,
  mergeGapReports,
  stripGapReports,
  restoreGapReports,
  removedAssessmentIds,
};
